import {
    Collapse,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    Navbar,
    NavbarToggler,
    NavItem
} from 'reactstrap';
import {Link, NavLink, useLocation, useNavigate} from 'react-router';
import ThemeContext from '../context/ThemeContext';
import {
    type CSSProperties,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import IconButton from './ui/IconButton';
import {
    MdDarkMode,
    MdKeyboardArrowDown,
    MdKeyboardArrowLeft,
    MdLightMode,
    MdMenu
} from 'react-icons/md';
import HelpButton from './ui/HelpButton';
import useClientConfig from '../lib/useClientConfig';
import {useIsSmallViewport} from '../lib/useMediaQuery';
import {useTeamsContext} from '../context/TeamsContext';
import {useAuthContext} from '../context/AuthContext';
import {NavigationRoutes} from '../NavigationRoutes.ts';
import {
    canAccessAnyAdminPage,
    getAdminSections,
    getAnalyticsPages
} from '../pages';
import {useFeatureFlags} from '../context/FeatureFlagsContext';
import {useDialogs} from './ui/DialogProvider';

interface NavigationProps {
    desktopOnly?: boolean;
    mobileOnly?: boolean;
    contextSlot?: ReactNode;
    contextSlotClassName?: string;
    toolbarClassName?: string;
    brandImageClassName?: string;
    brandImageStyle?: CSSProperties;
}

const Navigation: React.FC<NavigationProps> = ({
    desktopOnly = false,
    mobileOnly = false,
    contextSlot,
    contextSlotClassName = 'navbar-context-slot d-none d-lg-flex',
    toolbarClassName,
    brandImageClassName,
    brandImageStyle
}) => {
    const {
        isAuthenticated,
        pendingApprovalsCount,
        pendingCertificationsCount,
        isAuthorizedForResource,
        isThetaAdmin,
        logout,
        getAccessToken
    } = useAuthContext();
    const totalPendingCount =
        pendingApprovalsCount + pendingCertificationsCount;
    const {isEnabled} = useFeatureFlags();

    const themeContext = useContext(ThemeContext);
    const {theme, toggleTheme} = themeContext;

    const {logoLightModeSrc, logoDarkModeSrc, jobsEnabled} = useClientConfig();
    const {isRunningInTeams} = useTeamsContext();
    const isMobileNavigationViewport = useIsSmallViewport();

    const {confirm} = useDialogs();

    const [isOpen, setIsOpen] = useState(false);
    const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
    const [isAnalyticsDropdownOpen, setIsAnalyticsDropdownOpen] =
        useState(false);
    const [activeAdminSubmenuId, setActiveAdminSubmenuId] = useState<
        string | null
    >(null);
    const navigate = useNavigate();
    const location = useLocation();

    const toggle = () => setIsOpen(!isOpen);

    const closeAllNavMenus = () => {
        setActiveAdminSubmenuId(null);
        setIsAdminDropdownOpen(false);
        setIsAnalyticsDropdownOpen(false);
        setIsOpen(false);
    };

    // Sub-pages come from the same page definitions that build the routes, so the menu
    // cannot drift from what /analytics actually serves.
    const analyticsPages = useMemo(
        () =>
            getAnalyticsPages().map(page => ({
                path: page.path,
                label: page.label ?? 'Insights'
            })),
        []
    );

    useEffect(() => {
        closeAllNavMenus();
    }, [location.pathname]);

    useEffect(() => {
        const handleDocumentPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            if (!target.closest('.admin-nav-dropdown')) {
                setActiveAdminSubmenuId(null);
            }
        };

        document.addEventListener('mousedown', handleDocumentPointerDown);
        document.addEventListener('touchstart', handleDocumentPointerDown);

        return () => {
            document.removeEventListener(
                'mousedown',
                handleDocumentPointerDown
            );
            document.removeEventListener(
                'touchstart',
                handleDocumentPointerDown
            );
        };
    }, []);

    const handleLogout = async () => {
        try {
            // Invalidate the user cache on the server before logging out
            const token = await getAccessToken();
            if (token) {
                await fetch('/api/auth/invalidate-cache', {
                    method: 'POST',
                    headers: {Authorization: `Bearer ${token}`}
                });
            }
        } catch (error) {
            console.error('Failed to invalidate cache during logout:', error);
        } finally {
            logout();
        }
    };

    const handleLogoutClick = async (
        event: ReactMouseEvent<HTMLAnchorElement>
    ) => {
        const shouldConfirmLogout = mobileOnly || isMobileNavigationViewport;

        if (!shouldConfirmLogout) {
            void handleLogout();
            return;
        }

        event.preventDefault();
        closeAllNavMenus();
        if (await confirm('Are you sure you want to logout?')) {
            void handleLogout();
        }
    };

    // Check if user can access any admin page
    const canAccessAdmin = useMemo(
        () =>
            canAccessAnyAdminPage(
                isAuthorizedForResource,
                isEnabled,
                isThetaAdmin
            ),
        [isAuthorizedForResource, isEnabled, isThetaAdmin]
    );

    const adminSectionLinks = useMemo(() => {
        if (!canAccessAdmin) return [];

        return getAdminSections()
            .map(section => ({
                ...section,
                pages: section.pages.filter(page => {
                    if (!isAuthorizedForResource(page)) return false;
                    if (page.featureFlag && !isEnabled(page.featureFlag)) {
                        return false;
                    }
                    if (page.isThetaAdminOnly && !isThetaAdmin) return false;

                    return true;
                })
            }))
            .filter(section => section.pages.length > 0)
            .map(section => ({
                id: section.id,
                label: section.label,
                isDropdown: section.isDropdown,
                path: section.pages[0].path,
                pages: section.pages.map(page => ({
                    path: page.path,
                    label: page.label ?? section.label
                }))
            }))
            .filter(section => section.path);
    }, [canAccessAdmin, isAuthorizedForResource, isEnabled, isThetaAdmin]);

    const imgStyle = brandImageStyle ?? {
        height: 'var(--top-navigation-toolbar-brand-height)'
    };

    const visibilityClassName = desktopOnly
        ? 'top-navigation-toolbar--desktop-only d-none d-lg-flex'
        : mobileOnly
          ? 'top-navigation-toolbar--mobile-only d-lg-none'
          : '';

    const baseNavbarClassName = [
        'px-2 shadow-sm top-navigation-toolbar',
        visibilityClassName,
        isOpen && 'top-navigation-toolbar--menu-open',
        toolbarClassName
    ]
        .filter(Boolean)
        .join(' ');

    const navbarClassName = contextSlot
        ? `${baseNavbarClassName} top-navigation-toolbar--with-context`
        : baseNavbarClassName;

    const navigateToAdminPage = (
        event: ReactMouseEvent<HTMLElement>,
        path: string
    ) => {
        event.preventDefault();
        event.stopPropagation();
        navigate(path);
    };

    return (
        <Navbar
            expand={desktopOnly || mobileOnly ? 'lg' : 'sm'}
            className={navbarClassName}
            style={{paddingTop: '0.375rem', paddingBottom: '0.375rem'}}
        >
            <Link
                to={NavigationRoutes.home()}
                className="navbar-brand py-0"
                aria-label="Home Page"
                title="Home Page"
            >
                {
                    <img
                        style={imgStyle}
                        src={
                            theme === 'dark'
                                ? logoDarkModeSrc
                                : logoLightModeSrc
                        }
                        loading="lazy"
                        alt="Theta"
                        className={['brand-image', brandImageClassName]
                            .filter(Boolean)
                            .join(' ')}
                    ></img>
                }
            </Link>

            {contextSlot && (
                <div className={contextSlotClassName}>{contextSlot}</div>
            )}

            {isAuthenticated && (
                <NavbarToggler
                    className="btn-icon ms-auto me-2"
                    onClick={toggle}
                    aria-label="Menu"
                    title="Menu"
                >
                    <MdMenu size={24} />
                </NavbarToggler>
            )}

            <Collapse isOpen={isOpen} navbar>
                <Nav className="ms-auto" navbar>
                    {isAuthenticated && (
                        <>
                            <NavItem>
                                <NavLink
                                    to={NavigationRoutes.home()}
                                    className="text-decoration-none me-4"
                                >
                                    Home
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    to={NavigationRoutes.library()}
                                    className="text-decoration-none me-4"
                                >
                                    Library
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    to={NavigationRoutes.threads()}
                                    className="text-decoration-none me-4"
                                >
                                    Threads
                                </NavLink>
                            </NavItem>
                            {jobsEnabled && (
                                <NavItem>
                                    <NavLink
                                        to={NavigationRoutes.jobs.root()}
                                        className="text-decoration-none me-4"
                                        aria-label="Jobs"
                                    >
                                        Jobs
                                    </NavLink>
                                </NavItem>
                            )}
                            <NavItem className="admin-nav-item d-flex align-items-center me-4">
                                <NavLink
                                    to={NavigationRoutes.analytics.root()}
                                    className="text-decoration-none admin-nav-link"
                                    aria-label="Insights"
                                >
                                    Insights
                                </NavLink>
                                <Dropdown
                                    className="admin-nav-dropdown"
                                    isOpen={isAnalyticsDropdownOpen}
                                    toggle={() =>
                                        setIsAnalyticsDropdownOpen(
                                            prev => !prev
                                        )
                                    }
                                >
                                    <DropdownToggle
                                        tag="button"
                                        type="button"
                                        className="admin-nav-dropdown-toggle"
                                        aria-label="Insights Menu"
                                    >
                                        <MdKeyboardArrowDown
                                            aria-hidden="true"
                                            size={22}
                                        />
                                    </DropdownToggle>
                                    <DropdownMenu
                                        end
                                        className="dropdown-menu-nav admin-nav-dropdown-menu"
                                    >
                                        {analyticsPages.map(page => (
                                            <DropdownItem
                                                key={page.path}
                                                tag="button"
                                                type="button"
                                                onMouseDown={event =>
                                                    navigateToAdminPage(
                                                        event,
                                                        page.path
                                                    )
                                                }
                                                onClick={event =>
                                                    navigateToAdminPage(
                                                        event,
                                                        page.path
                                                    )
                                                }
                                            >
                                                {page.label}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </NavItem>
                            {canAccessAdmin && (
                                <NavItem className="admin-nav-item d-flex align-items-center me-4">
                                    <NavLink
                                        to={NavigationRoutes.admin.root()}
                                        className="text-decoration-none admin-nav-link"
                                        aria-label={
                                            totalPendingCount > 0
                                                ? `Admin, ${totalPendingCount} pending approval${
                                                      totalPendingCount === 1
                                                          ? ''
                                                          : 's'
                                                  }`
                                                : 'Admin'
                                        }
                                    >
                                        Admin {totalPendingCount > 0 && '🔔'}
                                    </NavLink>
                                    {adminSectionLinks.length > 0 && (
                                        <Dropdown
                                            className="admin-nav-dropdown"
                                            isOpen={isAdminDropdownOpen}
                                            toggle={() => {
                                                setIsAdminDropdownOpen(
                                                    prev => !prev
                                                );
                                                setActiveAdminSubmenuId(null);
                                            }}
                                        >
                                            <DropdownToggle
                                                tag="button"
                                                type="button"
                                                className="admin-nav-dropdown-toggle"
                                                aria-label="Admin Menu"
                                            >
                                                <MdKeyboardArrowDown
                                                    aria-hidden="true"
                                                    size={22}
                                                />
                                            </DropdownToggle>
                                            <DropdownMenu
                                                end
                                                className="dropdown-menu-nav admin-nav-dropdown-menu"
                                            >
                                                <div
                                                    className={
                                                        mobileOnly
                                                            ? 'admin-nav-desktop-menu d-none'
                                                            : 'admin-nav-desktop-menu d-none d-sm-block'
                                                    }
                                                >
                                                    {adminSectionLinks.map(
                                                        section =>
                                                            section.isDropdown &&
                                                            section.pages
                                                                .length > 1 ? (
                                                                <div
                                                                    key={
                                                                        section.id
                                                                    }
                                                                    className={`admin-nav-submenu${
                                                                        activeAdminSubmenuId ===
                                                                        section.id
                                                                            ? ' active'
                                                                            : ''
                                                                    }`}
                                                                    onMouseEnter={() =>
                                                                        setActiveAdminSubmenuId(
                                                                            section.id
                                                                        )
                                                                    }
                                                                    onFocus={() =>
                                                                        setActiveAdminSubmenuId(
                                                                            section.id
                                                                        )
                                                                    }
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="dropdown-item admin-nav-submenu-trigger"
                                                                        aria-haspopup="menu"
                                                                        onMouseDown={event =>
                                                                            navigateToAdminPage(
                                                                                event,
                                                                                section.path
                                                                            )
                                                                        }
                                                                        onClick={event =>
                                                                            navigateToAdminPage(
                                                                                event,
                                                                                section.path
                                                                            )
                                                                        }
                                                                    >
                                                                        <span>
                                                                            {
                                                                                section.label
                                                                            }
                                                                        </span>
                                                                        <MdKeyboardArrowLeft
                                                                            aria-hidden="true"
                                                                            size={
                                                                                18
                                                                            }
                                                                        />
                                                                    </button>
                                                                    <div
                                                                        className="dropdown-menu admin-nav-submenu-menu"
                                                                        role="menu"
                                                                    >
                                                                        {section.pages.map(
                                                                            page => (
                                                                                <button
                                                                                    key={
                                                                                        page.path
                                                                                    }
                                                                                    type="button"
                                                                                    className="dropdown-item"
                                                                                    onMouseDown={event =>
                                                                                        navigateToAdminPage(
                                                                                            event,
                                                                                            page.path
                                                                                        )
                                                                                    }
                                                                                    onClick={event =>
                                                                                        navigateToAdminPage(
                                                                                            event,
                                                                                            page.path
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        page.label
                                                                                    }
                                                                                </button>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <DropdownItem
                                                                    key={
                                                                        section.id
                                                                    }
                                                                    tag="button"
                                                                    type="button"
                                                                    onMouseDown={event =>
                                                                        navigateToAdminPage(
                                                                            event,
                                                                            section.path
                                                                        )
                                                                    }
                                                                    onClick={event =>
                                                                        navigateToAdminPage(
                                                                            event,
                                                                            section.path
                                                                        )
                                                                    }
                                                                    onMouseEnter={() =>
                                                                        setActiveAdminSubmenuId(
                                                                            null
                                                                        )
                                                                    }
                                                                    onFocus={() =>
                                                                        setActiveAdminSubmenuId(
                                                                            null
                                                                        )
                                                                    }
                                                                >
                                                                    {
                                                                        section.label
                                                                    }
                                                                </DropdownItem>
                                                            )
                                                    )}
                                                </div>
                                                <div
                                                    className={
                                                        mobileOnly
                                                            ? 'admin-nav-mobile-menu d-lg-none'
                                                            : 'admin-nav-mobile-menu d-sm-none'
                                                    }
                                                >
                                                    {adminSectionLinks.map(
                                                        section => (
                                                            <div
                                                                key={`${section.id}-mobile`}
                                                                className="admin-nav-mobile-section"
                                                            >
                                                                <div className="admin-nav-mobile-section-title">
                                                                    {
                                                                        section.label
                                                                    }
                                                                </div>
                                                                {section.pages.map(
                                                                    page => (
                                                                        <button
                                                                            key={
                                                                                page.path
                                                                            }
                                                                            type="button"
                                                                            className="dropdown-item admin-nav-mobile-page"
                                                                            onMouseDown={event =>
                                                                                navigateToAdminPage(
                                                                                    event,
                                                                                    page.path
                                                                                )
                                                                            }
                                                                            onClick={event =>
                                                                                navigateToAdminPage(
                                                                                    event,
                                                                                    page.path
                                                                                )
                                                                            }
                                                                        >
                                                                            {
                                                                                page.label
                                                                            }
                                                                        </button>
                                                                    )
                                                                )}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                </NavItem>
                            )}
                            {!isRunningInTeams && (
                                <NavItem>
                                    <a
                                        href="#"
                                        onClick={handleLogoutClick}
                                        className="text-decoration-none me-4"
                                    >
                                        Logout
                                    </a>
                                </NavItem>
                            )}
                            <NavItem
                                className={
                                    mobileOnly
                                        ? 'mobile-nav-actions d-flex d-lg-none'
                                        : 'mobile-nav-actions d-sm-none'
                                }
                            >
                                <HelpButton className="mobile-nav-action-button btn-icon" />
                                <IconButton
                                    className="mobile-nav-action-button"
                                    icon={
                                        theme === 'dark' ? (
                                            <MdLightMode />
                                        ) : (
                                            <MdDarkMode />
                                        )
                                    }
                                    onClick={toggleTheme}
                                    aria-label="Light/Dark Mode"
                                    title="Light/Dark Mode"
                                />
                            </NavItem>
                        </>
                    )}
                </Nav>
            </Collapse>

            <HelpButton
                className={
                    mobileOnly
                        ? 'btn-icon me-2 d-none'
                        : 'btn-icon me-2 d-none d-sm-flex'
                }
            />
            <IconButton
                className={mobileOnly ? 'd-none' : 'd-none d-sm-flex'}
                icon={theme === 'dark' ? <MdLightMode /> : <MdDarkMode />}
                onClick={toggleTheme}
                aria-label="Light/Dark Mode"
                title="Light/Dark Mode"
            />
        </Navbar>
    );
};

export default Navigation;
